import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const API = '/api/v1/fsm/reports';
const CHART_COLORS = ['#6366f1','#10b981','#f59e0b','#ef4444','#8b5cf6','#06b6d4','#f97316','#ec4899'];
const COMPLEXITY_COLORS = { light: '#10b981', medium: '#f59e0b', heavy: '#ef4444' };

const CATEGORY_LABELS = {
  all:         { en: 'All Reports',     ar: 'جميع التقارير' },
  work_orders: { en: 'Work Orders',     ar: 'أوامر العمل' },
  sla:         { en: 'SLA & Contracts', ar: 'SLA والعقود' },
  technicians: { en: 'Technicians',     ar: 'الفنيون' },
  equipment:   { en: 'Equipment',       ar: 'المعدات' },
  clients:     { en: 'Clients',         ar: 'العملاء' },
  financial:   { en: 'Financial',       ar: 'المالية' },
};

const FilterControl = ({ filter, filters, onChange, isRTL, inputStyle }) => {
  const label = isRTL && filter.label_ar ? filter.label_ar : (filter.label || filter.field);
  const presetKey = `${filter.field}_preset`;

  if (filter.controlType === 'date_preset') {
    return (
      <div>
        <label style={{ fontSize: 11, color: '#9ca3af', display: 'block', marginBottom: 4 }}>{label}</label>
        <select value={filters[presetKey] || 30} onChange={e => onChange(presetKey, parseInt(e.target.value))} style={{ ...inputStyle, cursor: 'pointer' }}>
          {[7, 14, 30, 60, 90, 180, 365].map(n => (
            <option key={n} value={n}>{isRTL ? `آخر ${n} يوم` : `Last ${n} days`}</option>
          ))}
        </select>
      </div>
    );
  }
  if (filter.controlType === 'select') {
    return (
      <div>
        <label style={{ fontSize: 11, color: '#9ca3af', display: 'block', marginBottom: 4 }}>{label}</label>
        <select value={filters[filter.field] || ''} onChange={e => onChange(filter.field, e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
          <option value="">{isRTL ? 'الكل' : 'All'}</option>
          {(filter.options || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      </div>
    );
  }
  if (filter.controlType === 'multi_select') {
    const selected = Array.isArray(filters[filter.field]) ? filters[filter.field] : (filter.value || []);
    return (
      <div>
        <label style={{ fontSize: 11, color: '#9ca3af', display: 'block', marginBottom: 4 }}>{label}</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {(filter.options || []).map(opt => {
            const active = selected.includes(opt);
            return (
              <button key={opt} type="button"
                onClick={() => onChange(filter.field, active ? selected.filter(s => s !== opt) : [...selected, opt])}
                style={{ padding: '3px 10px', borderRadius: 12, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600,
                  background: active ? '#6366f1' : '#1f2937', color: active ? '#fff' : '#9ca3af' }}>
                {opt}
              </button>
            );
          })}
        </div>
      </div>
    );
  }
  if (filter.controlType === 'date_range') {
    const fromKey = `${filter.field}_from`;
    const toKey = `${filter.field}_to`;
    return (
      <div>
        <label style={{ fontSize: 11, color: '#9ca3af', display: 'block', marginBottom: 4 }}>{label}</label>
        <div style={{ display: 'flex', gap: 6 }}>
          <input type="date" value={filters[fromKey] || ''} onChange={e => onChange(fromKey, e.target.value)}
            style={{ ...inputStyle, flex: 1 }} />
          <input type="date" value={filters[toKey] || ''} onChange={e => onChange(toKey, e.target.value)}
            style={{ ...inputStyle, flex: 1 }} />
        </div>
      </div>
    );
  }
  if (filter.controlType === 'number_range') {
    const minKey = `${filter.field}_min`;
    const maxKey = `${filter.field}_max`;
    return (
      <div>
        <label style={{ fontSize: 11, color: '#9ca3af', display: 'block', marginBottom: 4 }}>{label}</label>
        <div style={{ display: 'flex', gap: 6 }}>
          <input type="number" value={filters[minKey] || ''} onChange={e => onChange(minKey, e.target.value)}
            style={{ ...inputStyle, flex: 1 }} placeholder={isRTL ? 'من' : 'Min'} />
          <input type="number" value={filters[maxKey] || ''} onChange={e => onChange(maxKey, e.target.value)}
            style={{ ...inputStyle, flex: 1 }} placeholder={isRTL ? 'إلى' : 'Max'} />
        </div>
      </div>
    );
  }
  return (
    <div>
      <label style={{ fontSize: 11, color: '#9ca3af', display: 'block', marginBottom: 4 }}>{label}</label>
      <input value={filters[filter.field] || ''} onChange={e => onChange(filter.field, e.target.value)} style={inputStyle} placeholder={label} />
    </div>
  );
};

const ReportChart = ({ chartConfig, chartData, columns }) => {
  const type   = chartConfig?.type  || 'bar';
  const height = chartConfig?.height || 350;
  const xKey   = chartConfig?.xAxis || (columns[0]?.key || 'label');
  const yKey   = chartConfig?.yAxis || (columns[1]?.key || 'value');

  if (type === 'pie' || type === 'doughnut') {
    const data = chartData.map(d => ({ name: String(d[xKey] || '?'), value: parseFloat(d[yKey]) || 0 }));
    return (
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie data={data} cx="50%" cy="50%" innerRadius={type === 'doughnut' ? 70 : 0} outerRadius={130}
            dataKey="value" label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`} labelLine={false}>
            {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
          </Pie>
          <Tooltip contentStyle={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 8 }} />
          {chartConfig?.showLegend && <Legend />}
        </PieChart>
      </ResponsiveContainer>
    );
  }
  if (type === 'horizontal_bar') {
    return (
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 30, left: 100, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
          <XAxis type="number" tick={{ fill: '#6b7280', fontSize: 11 }} />
          <YAxis type="category" dataKey={xKey} tick={{ fill: '#9ca3af', fontSize: 11 }} width={100} />
          <Tooltip contentStyle={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 8 }} />
          <Bar dataKey={yKey} fill="#6366f1" radius={[0,4,4,0]} />
        </BarChart>
      </ResponsiveContainer>
    );
  }
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 60 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
        <XAxis dataKey={xKey} tick={{ fill: '#6b7280', fontSize: 11, angle: -30, textAnchor: 'end' }} />
        <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} />
        <Tooltip contentStyle={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 8 }} />
        {chartConfig?.showLegend && <Legend />}
        <Bar dataKey={yKey} fill="#6366f1" radius={[4,4,0,0]} />
      </BarChart>
    </ResponsiveContainer>
  );
};


const BuilderPanel = ({ report, config, onConfigChange, onSaveAs, isRTL, inputStyle }) => {
  const [metadata, setMetadata] = useState(null);
  const [sourceColumns, setSourceColumns] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveAsName, setSaveAsName] = useState('');
  const [showSaveAs, setShowSaveAs] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${API}/metadata/sources`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(setMetadata).catch(() => {});
  }, []);

  const loadColumns = async (sourceName) => {
    if (sourceColumns[sourceName]) return;
    const token = localStorage.getItem('token');
    const res = await fetch(`${API}/metadata/source/${sourceName}/columns?visibleOnly=true`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const cols = await res.json();
    setSourceColumns(prev => ({ ...prev, [sourceName]: cols }));
  };

  const currentDatasources = config.datasources || [];
  const currentColumns = config.columns || [];
  const currentGroupBy = config.groupBy || [];
  const currentAggs = config.aggregations || [];
  const chartType = (typeof report.chart_config === 'string' ? JSON.parse(report.chart_config || '{}') : (report.chart_config || {})).type || 'bar';

  const toggleSource = (srcKey) => {
    const updated = currentDatasources.includes(srcKey)
      ? currentDatasources.filter(s => s !== srcKey)
      : [...currentDatasources, srcKey];
    onConfigChange({ ...config, datasources: updated });
    if (!currentDatasources.includes(srcKey)) loadColumns(srcKey);
  };

  const toggleColumn = (source, field, colMeta) => {
    const key = `${source}.${field}`;
    const exists = currentColumns.find(c => c.source === source && c.field === field);
    if (exists) {
      onConfigChange({ ...config, columns: currentColumns.filter(c => !(c.source === source && c.field === field)) });
    } else {
      onConfigChange({ ...config, columns: [...currentColumns, {
        source, field, alias: colMeta.label, alias_ar: colMeta.label_ar
      }]});
    }
  };

  const toggleGroupBy = (source, field) => {
    const key = `${source}.${field}`;
    const exists = currentGroupBy.includes(key);
    onConfigChange({ ...config, groupBy: exists ? currentGroupBy.filter(g => g !== key) : [...currentGroupBy, key] });
  };

  const setAggregation = (source, field, fn, label, labelAr) => {
    const exists = currentAggs.find(a => a.source === source && a.field === field);
    if (exists && exists.fn === fn) {
      onConfigChange({ ...config, aggregations: currentAggs.filter(a => !(a.source === source && a.field === field)) });
    } else {
      const newAggs = currentAggs.filter(a => !(a.source === source && a.field === field));
      if (fn) newAggs.push({ source, field, fn, alias: `${fn}(${label})`, alias_ar: `${fn}(${labelAr || label})` });
      onConfigChange({ ...config, aggregations: newAggs });
    }
  };

  const handleSaveAs = async () => {
    if (!saveAsName.trim()) return;
    setSaving(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/definitions/${report.id}/duplicate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: saveAsName, report_config: config }),
      });
      const data = await res.json();
      if (res.ok) {
        alert(isRTL ? `✅ تم الحفظ: ${saveAsName}` : `✅ Saved: ${saveAsName}`);
        setShowSaveAs(false);
        setSaveAsName('');
        if (onSaveAs) onSaveAs(data);
      } else {
        alert(data.error || 'Save failed');
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const sectionStyle = { background: '#111827', borderRadius: 10, padding: 14, marginBottom: 12 };
  const sectionTitle = { fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.5px' };
  const chipActive = { padding: '4px 10px', borderRadius: 12, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600, background: '#6366f1', color: '#fff' };
  const chipInactive = { ...chipActive, background: '#1f2937', color: '#9ca3af' };
  const aggFns = ['COUNT', 'SUM', 'AVG', 'MIN', 'MAX'];

  // Load columns for current datasources on mount
  useEffect(() => {
    currentDatasources.forEach(ds => loadColumns(ds));
  }, []);

  return (
    <div style={{ background: '#0d1117', border: '1px solid rgba(99,102,241,0.3)', borderRadius: 14, padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: '#f9fafb' }}>
          {isRTL ? '✏️ تخصيص التقرير' : '✏️ Customize Report'}
        </span>
        <button type="button" onClick={() => setShowSaveAs(!showSaveAs)}
          style={{ padding: '6px 16px', borderRadius: 8, border: '1px solid #6366f1', background: 'transparent',
            color: '#6366f1', cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>
          {isRTL ? '💾 حفظ باسم' : '💾 Save As'}
        </button>
      </div>

      {showSaveAs && (
        <div style={{ ...sectionStyle, border: '1px solid #6366f1', display: 'flex', gap: 8, alignItems: 'center' }}>
          <input value={saveAsName} onChange={e => setSaveAsName(e.target.value)}
            style={{ ...inputStyle, flex: 1 }}
            placeholder={isRTL ? 'اسم التقرير الجديد...' : 'New report name...'} />
          <button type="button" onClick={handleSaveAs} disabled={saving || !saveAsName.trim()}
            style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#6366f1',
              color: '#fff', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 12, whiteSpace: 'nowrap' }}>
            {saving ? '...' : (isRTL ? '💾 حفظ' : '💾 Save')}
          </button>
        </div>
      )}

      {/* Data Sources */}
      <div style={sectionStyle}>
        <div style={sectionTitle}>{isRTL ? '📦 مصادر البيانات' : '📦 Data Sources'}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {(metadata || []).map(src => (
            <button key={src.key} type="button" onClick={() => toggleSource(src.key)}
              style={currentDatasources.includes(src.key) ? chipActive : chipInactive}>
              {isRTL ? src.label_ar : src.label} ({src.columnCount})
            </button>
          ))}
        </div>
      </div>

      {/* Columns */}
      <div style={sectionStyle}>
        <div style={sectionTitle}>{isRTL ? '📋 الأعمدة' : '📋 Columns'}</div>
        {currentDatasources.map(ds => {
          const cols = sourceColumns[ds] || [];
          if (cols.length === 0) return null;
          const srcMeta = (metadata || []).find(m => m.key === ds);
          return (
            <div key={ds} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, color: '#818cf8', fontWeight: 600, marginBottom: 6 }}>
                {isRTL && srcMeta?.label_ar ? srcMeta.label_ar : (srcMeta?.label || ds)}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {cols.map(col => {
                  const isSelected = currentColumns.find(c => c.source === ds && c.field === col.field);
                  return (
                    <button key={col.field} type="button" onClick={() => toggleColumn(ds, col.field, col)}
                      style={isSelected ? chipActive : chipInactive}>
                      {isRTL ? (col.label_ar || col.label) : col.label}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Group By */}
      <div style={sectionStyle}>
        <div style={sectionTitle}>{isRTL ? '📊 تجميع حسب' : '📊 Group By'}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {currentColumns.map(col => {
            const key = `${col.source}.${col.field}`;
            const isGrouped = currentGroupBy.includes(key);
            return (
              <button key={key} type="button" onClick={() => toggleGroupBy(col.source, col.field)}
                style={isGrouped ? { ...chipActive, background: '#059669' } : chipInactive}>
                {isRTL ? (col.alias_ar || col.alias) : col.alias}
              </button>
            );
          })}
        </div>
        {currentColumns.length === 0 && (
          <div style={{ fontSize: 11, color: '#4b5563' }}>{isRTL ? 'أضف أعمدة أولاً' : 'Add columns first'}</div>
        )}
      </div>

      {/* Aggregations */}
      <div style={sectionStyle}>
        <div style={sectionTitle}>{isRTL ? '🔢 الحسابات' : '🔢 Aggregations'}</div>
        {currentDatasources.map(ds => {
          const cols = (sourceColumns[ds] || []).filter(c => c.aggregatable);
          if (cols.length === 0) return null;
          return cols.map(col => {
            const currentAgg = currentAggs.find(a => a.source === ds && a.field === col.field);
            return (
              <div key={`${ds}.${col.field}`} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <span style={{ fontSize: 11, color: '#9ca3af', minWidth: 100 }}>{isRTL ? (col.label_ar || col.label) : col.label}</span>
                {aggFns.map(fn => (
                  <button key={fn} type="button"
                    onClick={() => setAggregation(ds, col.field, fn, col.label, col.label_ar)}
                    style={(currentAgg && currentAgg.fn === fn) ? { ...chipActive, background: '#b45309', padding: '2px 8px', fontSize: 10 } : { ...chipInactive, padding: '2px 8px', fontSize: 10 }}>
                    {fn}
                  </button>
                ))}
              </div>
            );
          });
        })}
      </div>

      {/* Chart Type */}
      <div style={sectionStyle}>
        <div style={sectionTitle}>{isRTL ? '📈 نوع الرسم البياني' : '📈 Chart Type'}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {[
            { key: 'bar', label: '📊 Bar', label_ar: '📊 أعمدة' },
            { key: 'horizontal_bar', label: '📊 H-Bar', label_ar: '📊 أفقي' },
            { key: 'line', label: '📈 Line', label_ar: '📈 خط' },
            { key: 'pie', label: '🥧 Pie', label_ar: '🥧 دائري' },
            { key: 'doughnut', label: '🍩 Donut', label_ar: '🍩 حلقي' },
            { key: 'stacked_bar', label: '📊 Stacked', label_ar: '📊 مكدس' },
            { key: null, label: '❌ None', label_ar: '❌ بدون' },
          ].map(ct => (
            <button key={ct.key || 'none'} type="button"
              onClick={() => {
                const newChart = ct.key ? { ...(typeof report.chart_config === 'string' ? JSON.parse(report.chart_config || '{}') : (report.chart_config || {})), type: ct.key } : null;
                report.chart_config = newChart;
                onConfigChange({ ...config });
              }}
              style={chartType === ct.key ? chipActive : chipInactive}>
              {isRTL ? ct.label_ar : ct.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const RunnerPanel = ({ report, filters, setFilters, running, results, runError, viewMode, setViewMode, onRun, onClose, onExport, onSaveAs, onConfigChange, liveConfig, isRTL, inputStyle }) => {
  const handleExport = (format) => { if (onExport) onExport(format); };
  const [showBuilder, setShowBuilder] = useState(false);
  const config      = typeof report.report_config === 'string' ? JSON.parse(report.report_config) : (report.report_config || {});
  const chartConfig = typeof report.chart_config  === 'string' ? JSON.parse(report.chart_config)  : report.chart_config;
  const editableFilters = (config.filters || []).filter(fm => fm.userEditable);
  const rows       = results?.data?.rows      || [];
  const columns    = results?.data?.columns   || [];
  const totalRows  = results?.data?.totalRows || 0;
  const execMs     = results?.executionMs     || 0;

  const chartData = rows.slice(0, 30).map(row => {
    if (!Array.isArray(row)) return row; // already an object
    const obj = {};
    columns.forEach((col, ci) => { obj[col.key] = row[ci]; });
    return obj;
  });

  return (
    <div style={{ background: '#0d1117', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 16, padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <span style={{ fontSize: 28 }}>{report.icon || '📊'}</span>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#f9fafb' }}>
              {isRTL && report.name_ar ? report.name_ar : report.name}
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: '#6b7280', lineHeight: 1.5, maxWidth: 600 }}>
              {isRTL && report.description_ar ? report.description_ar : report.description}
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button type="button" onClick={() => setShowBuilder(!showBuilder)} title={isRTL ? 'تخصيص' : 'Customize'}
            style={{ background: showBuilder ? '#1e1b4b' : 'transparent', border: showBuilder ? '1px solid #6366f1' : '1px solid rgba(255,255,255,0.1)',
              color: showBuilder ? '#a5b4fc' : '#4b5563', cursor: 'pointer', fontSize: 14, padding: '4px 8px', borderRadius: 6, transition: 'all 0.2s' }}>✏️</button>
          <button type="button" onClick={onClose}
            style={{ background: 'none', border: 'none', color: '#4b5563', cursor: 'pointer', fontSize: 20, padding: 4 }}>✕</button>
        </div>
      </div>

      {editableFilters.length > 0 && (
        <div style={{ background: '#111827', borderRadius: 12, padding: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {isRTL ? '⚙ فلاتر التقرير' : '⚙ Report Filters'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14 }}>
            {editableFilters.map((filter, idx) => (
              <FilterControl key={idx} filter={filter} filters={filters}
                onChange={(k, v) => setFilters(prev => ({ ...prev, [k]: v }))}
                isRTL={isRTL} inputStyle={inputStyle} />
            ))}
          </div>
        </div>
      )}

      {showBuilder && liveConfig && (
        <BuilderPanel report={report} config={liveConfig} onConfigChange={onConfigChange}
          onSaveAs={onSaveAs} isRTL={isRTL} inputStyle={inputStyle} />
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button type="button" onClick={onRun} disabled={running}
          style={{ background: running ? '#374151' : 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff', border: 'none',
            borderRadius: 8, padding: '10px 28px', fontWeight: 700, fontSize: 14, cursor: running ? 'not-allowed' : 'pointer' }}>
          {running ? (isRTL ? 'جاري التنفيذ...' : 'Running...') : (isRTL ? '▶ تشغيل التقرير' : '▶ Run Report')}
        </button>
        {results && (
          <div style={{ display: 'flex', gap: 6, marginInlineStart: 'auto', flexWrap: 'wrap' }}>
            {chartConfig && [['table', isRTL ? '📋 جدول' : '📋 Table'], ['chart', isRTL ? '📊 رسم' : '📊 Chart']]
              .map(([mode, lbl]) => (
              <button key={mode} type="button" onClick={() => setViewMode(mode)}
                style={{ padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13,
                  background: viewMode === mode ? '#6366f1' : '#1f2937', color: '#fff' }}>
                {lbl}
              </button>
            ))}
            {chartConfig && <span style={{ width: 1, background: '#374151', margin: '0 2px' }} />}
            <button type="button" onClick={() => handleExport('xlsx')}
              style={{ padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13,
                background: '#065f46', color: '#fff' }}>
              {isRTL ? '📥 اكسل' : '📥 Excel'}
            </button>
            <button type="button" onClick={() => handleExport('pdf')}
              style={{ padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13,
                background: '#7f1d1d', color: '#fff' }}>
              {isRTL ? '📄 PDF' : '📄 PDF'}
            </button>
          </div>
        )}
      </div>

      {runError && (
        <div style={{ background: '#450a0a', border: '1px solid #ef4444', borderRadius: 8, padding: 12, color: '#fca5a5', fontSize: 13 }}>
          ❌ {runError}
        </div>
      )}

      {results && !runError && (
        <div>
          <div style={{ fontSize: 12, color: '#4b5563', marginBottom: 12 }}>
            {isRTL ? `${totalRows} نتيجة · ${execMs}ms` : `${totalRows} rows · ${execMs}ms`}
          </div>

          {viewMode === 'chart' && chartConfig && chartData.length > 0 && (
            <div style={{ background: '#111827', borderRadius: 12, padding: 20, marginBottom: 16 }}>
              <ReportChart chartConfig={chartConfig} chartData={chartData} columns={columns} />
            </div>
          )}

          {viewMode === 'table' && (
            <div style={{ overflowX: 'auto', borderRadius: 12, border: '1px solid rgba(255,255,255,0.07)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#1f2937' }}>
                    {columns.map((col, i) => (
                      <th key={i} style={{ padding: '10px 14px', textAlign: isRTL ? 'right' : 'left', color: '#9ca3af',
                        fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.3px', whiteSpace: 'nowrap' }}>
                        {isRTL && col.label_ar ? col.label_ar : (col.label || col.key)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, ri) => {
                    const cells = Array.isArray(row)
                      ? row
                      : columns.map(col => row[col.key] !== undefined ? row[col.key] : row[col.label]);
                    return (
                      <tr key={ri} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)',
                        background: ri % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)' }}>
                        {cells.map((cell, ci) => (
                          <td key={ci} style={{ padding: '9px 14px', color: cell == null ? '#4b5563' : '#e0e0e0' }}>
                            {cell == null ? '—' : String(cell)}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={columns.length || 1}
                        style={{ textAlign: 'center', padding: 48, color: '#4b5563', fontSize: 14 }}>
                        {isRTL ? 'لا توجد نتائج' : 'No results found'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const FSMReports = () => {
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const isRTL = lang === 'ar';

  const [kpis,    setKpis]    = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [search,  setSearch]  = useState('');

  const [selectedReport, setSelectedReport] = useState(null);
  const [liveConfig, setLiveConfig] = useState(null);
  const [runFilters,     setRunFilters]      = useState({});
  const [running,        setRunning]         = useState(false);
  const [results,        setResults]         = useState(null);
  const [viewMode,       setViewMode]        = useState('table');
  const [runError,       setRunError]        = useState(null);

  useEffect(() => {
    const loadData = async () => {
      const token = localStorage.getItem('token');
      setLoading(true);
      try {
        const [catRes, kpiRes] = await Promise.all([
          fetch(`${API}/catalog`,        { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API}/dashboard/kpis`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        const catData = await catRes.json();
        const kpiData = await kpiRes.json();
        setCatalog(catData.reports || []);
        setGrouped(catData.grouped  || {});
        setKpis(kpiData);
      } catch (err) {
        console.error('Load error:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const visibleReports = catalog.filter(r => {
    const matchCat    = activeCategory === 'all' || r.category === activeCategory;
    const matchSearch = !search
      || r.name.toLowerCase().includes(search.toLowerCase())
      || (r.name_ar && r.name_ar.includes(search));
    return matchCat && matchSearch;
  });

  const openReport = async (report) => {
    setSelectedReport(report);
    setResults(null);
    setRunError(null);
    setViewMode('table');

    // Load full definition with report_config
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/definitions/${report.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const fullDef = await res.json();
      const config = typeof fullDef.report_config === 'string'
        ? JSON.parse(fullDef.report_config) : (fullDef.report_config || {});
      const chartCfg = typeof fullDef.chart_config === 'string'
        ? JSON.parse(fullDef.chart_config || 'null') : fullDef.chart_config;

      // Merge full data into selected report
      const enriched = { ...report, ...fullDef, report_config: config, chart_config: chartCfg };
      setSelectedReport(enriched);
      setLiveConfig(JSON.parse(JSON.stringify(config)));

      const init = {};
      (config.filters || []).forEach(fm => {
        if (!fm.userEditable) return;
        if (fm.controlType === 'date_preset')      init[`${fm.field}_preset`] = fm.value || 30;
        else if (fm.controlType === 'multi_select') init[fm.field] = fm.value || [];
        else                                        init[fm.field] = fm.value || '';
      });
      setRunFilters(init);
    } catch (err) {
      console.error('Failed to load report definition:', err);
      setRunError(err.message);
    }
  };

  const runReport = async () => {
    const token = localStorage.getItem('token');
    setRunning(true);
    setRunError(null);
    try {
      const res = await fetch(`${API}/execute`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ report_definition_id: selectedReport.id, report_config: liveConfig, filters: runFilters, page: 1, pageSize: 500 }),
      });
      const data = await res.json();
      if (!res.ok) { setRunError(data.error || 'Execution failed'); return; }
      setResults(data);
      if (data.chart_config || selectedReport.chart_config) setViewMode('chart');
    } catch (err) {
      setRunError(err.message);
    } finally {
      setRunning(false);
    }
  };

  const inputStyle = {
    backgroundColor: '#1a2038', border: '2px solid rgba(255,255,255,0.2)',
    borderRadius: 8, color: '#e0e0e0', padding: '8px 12px',
    fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box',
  };

  const handleExport = async (format) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/execute/export`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept-Language': lang,
        },
        body: JSON.stringify({
          report_definition_id: selectedReport?.id,
          filters: runFilters,
          format,
        }),
      });
      if (!response.ok) {
        const err = await response.json();
        alert(err.error || 'Export failed');
        return;
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const name = (selectedReport?.name || 'report').replace(/[^a-zA-Z0-9_-]/g, '_');
      a.download = `${name}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.message || 'Export failed');
    }
  };

  if (loading) return (
    <Layout>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div style={{ color: '#6366f1', fontSize: 16 }}>{isRTL ? 'جاري التحميل...' : 'Loading reports...'}</div>
      </div>
    </Layout>
  );

  return (
    <Layout>
      <div style={{ padding: 24, direction: isRTL ? 'rtl' : 'ltr', minHeight: '100vh', background: '#0a0f1e' }}>

        <div style={{ marginBottom: 24 }}>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#f9fafb' }}>
            📊 {isRTL ? 'نظام التقارير' : 'Reports & Analytics'}
          </h1>
          <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 14 }}>
            {isRTL ? '32 تقرير جاهز للتشغيل عبر 6 فئات' : '32 ready-to-run reports across 6 categories'}
          </p>
        </div>

        {kpis && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 14, marginBottom: 28 }}>
            {[
              { label: isRTL ? 'أوامر مفتوحة'     : 'Open WOs',         value: kpis.openWorkOrders,      color: '#6366f1', icon: '🔧' },
              { label: isRTL ? 'متأخرة'            : 'Overdue WOs',      value: kpis.overdueWorkOrders,   color: '#ef4444', icon: '🚨' },
              { label: isRTL ? 'عقود نشطة'         : 'Active Contracts', value: kpis.activeContracts,     color: '#10b981', icon: '📋' },
              { label: isRTL ? 'الالتزام بـ SLA'   : 'SLA Compliance',   value: `${kpis.slaCompliancePct}%`, color: '#f59e0b', icon: '🎯' },
              { label: isRTL ? 'متوسط وقت الإصلاح' : 'Avg MTTR',        value: `${kpis.avgMttrHours}h`,  color: '#8b5cf6', icon: '⏱️' },
            ].map(kpi => (
              <div key={kpi.label} style={{ background: '#111827', border: `1px solid ${kpi.color}33`, borderRadius: 12, padding: '16px 18px' }}>
                <div style={{ fontSize: 22, marginBottom: 6 }}>{kpi.icon}</div>
                <div style={{ fontSize: 26, fontWeight: 700, color: kpi.color }}>{kpi.value}</div>
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>{kpi.label}</div>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: selectedReport ? '340px 1fr' : '1fr', gap: 20, alignItems: 'start' }}>

          <div>
            <input style={{ ...inputStyle, marginBottom: 14 }}
              placeholder={isRTL ? '🔍 ابحث في التقارير...' : '🔍 Search reports...'}
              value={search} onChange={e => setSearch(e.target.value)} />

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
              {Object.entries(CATEGORY_LABELS).map(([key, labels]) => {
                const count    = key === 'all' ? catalog.length : (grouped[key] || []).length;
                const isActive = activeCategory === key;
                return (
                  <button key={key} type="button" onClick={() => setActiveCategory(key)}
                    style={{ padding: '5px 14px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                      background: isActive ? '#6366f1' : '#1f2937', color: isActive ? '#fff' : '#9ca3af', transition: 'all 0.2s' }}>
                    {isRTL ? labels.ar : labels.en}
                    {count > 0 && <span style={{ opacity: 0.7, marginInlineStart: 4 }}>({count})</span>}
                  </button>
                );
              })}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 'calc(100vh - 420px)', overflowY: 'auto', paddingRight: 4 }}>
              {/* Preventive Maintenance Schedule - Featured Report */}
              <div onClick={() => navigate('/fsm/preventive-schedule')}
                style={{ background: '#111827',
                  border: '1px solid rgba(255,255,255,0.07)',
                  borderRadius: 10, padding: '14px 16px', cursor: 'pointer', transition: 'all 0.15s' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>📅</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: '#f9fafb', lineHeight: 1.3 }}>
                      {isRTL ? 'جدول الصيانة الوقائية' : 'Preventive Maintenance Schedule'}
                    </div>
                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3, lineHeight: 1.4,
                      overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                      {isRTL ? 'أعمال الصيانة المطلوبة من واقع خطط الصيانة المرتبطة بالعقود السارية' : 'All scheduled maintenance from active contract plans'}
                    </div>
                    <div style={{ display: 'flex', gap: 5, marginTop: 7, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: '#0f172a',
                        color: '#6366f1', fontWeight: 700 }}>
                        {isRTL ? 'نظام' : 'System'}
                      </span>
                      <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: '#0f172a',
                        color: '#10b981', fontWeight: 600 }}>
                        light
                      </span>
                    </div>
                  </div>
                  <span style={{ color: '#4b5563', fontSize: 16, flexShrink: 0 }}>›</span>
                </div>
              </div>

              {visibleReports.map(report => {
                const isSel = selectedReport?.id === report.id;
                return (
                  <div key={report.id} onClick={() => window.open(`/fsm/reports/${report.id}`, '_blank')}
                    style={{ background: isSel ? '#1a1f3a' : '#111827',
                      border: `1px solid ${isSel ? '#6366f1' : 'rgba(255,255,255,0.07)'}`,
                      borderRadius: 10, padding: '14px 16px', cursor: 'pointer', transition: 'all 0.15s' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>{report.icon || '📊'}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, color: '#f9fafb', lineHeight: 1.3 }}>
                          {isRTL && report.name_ar ? report.name_ar : report.name}
                        </div>
                        {report.description && (
                          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3, lineHeight: 1.4,
                            overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                            {isRTL && report.description_ar ? report.description_ar : report.description}
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: 5, marginTop: 7, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: '#0f172a',
                            color: report.is_system ? '#6366f1' : '#10b981', fontWeight: 700 }}>
                            {report.is_system ? (isRTL ? 'نظام' : 'System') : (isRTL ? 'مخصص' : 'Custom')}
                          </span>
                          <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: '#0f172a',
                            color: COMPLEXITY_COLORS[report.estimated_complexity] || '#9ca3af', fontWeight: 600 }}>
                            {report.estimated_complexity || 'light'}
                          </span>
                        </div>
                      </div>
                      <span style={{ color: '#4b5563', fontSize: 16, flexShrink: 0 }}>›</span>
                    </div>
                  </div>
                );
              })}
              {visibleReports.length === 0 && (
                <div style={{ textAlign: 'center', color: '#374151', padding: '48px 20px', fontSize: 14 }}>
                  {isRTL ? 'لا توجد تقارير' : 'No reports found'}
                </div>
              )}
            </div>
          </div>

          {selectedReport && (
            <RunnerPanel
              report={selectedReport}
              filters={runFilters}
              setFilters={setRunFilters}
              running={running}
              results={results}
              runError={runError}
              viewMode={viewMode}
              setViewMode={setViewMode}
              onRun={runReport}
              onExport={handleExport}
              onClose={() => { setSelectedReport(null); setResults(null); setLiveConfig(null); }}
              onConfigChange={setLiveConfig}
              onSaveAs={(data) => {
                // Refresh catalog after save
                const token = localStorage.getItem('token');
                fetch(`${API}/catalog`, { headers: { Authorization: `Bearer ${token}` } })
                  .then(r => r.json()).then(d => { setCatalog(d.reports || []); setGrouped(d.grouped || {}); });
              }}
              liveConfig={liveConfig}
              isRTL={isRTL}
              inputStyle={inputStyle}
            />
          )}
        </div>
      </div>
    </Layout>
  );
};

export default FSMReports;
